import { useQuery } from "@tanstack/react-query";
import { SiteReadable } from "../../../services/client";
import { getMeasurementsForSiteInArrowOptions } from "../../../services/client/@tanstack/react-query.gen";
import {
  getAllCollectors,
  getChargingAssetNames,
  getDeviceDistribution,
} from "../../../util/siteUtils";

import { TimeSeriesChart } from "../Charts/TimeSeriesChart";
import { useMapResponseToTimeseries } from "./useMapResponseToTimeseries";
import { useMapBessResponseToTimeseries } from "./useMapBessResponseToTimeseries";

type DevicesChartProps = {
  site: SiteReadable;
  from: Date;
  to: Date;
};

const chartColors = ["#1e8c6f", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd"];

export const DetailedCharts: React.FC<DevicesChartProps> = ({
  site,
  from,
  to,
}) => {
  const deviceDistribution = getDeviceDistribution(site);
  const collectors = getAllCollectors(site);
  const chargingAssets = getChargingAssetNames(site);

  const {
    data: chargingStations,
    isLoading: chargingStationsLoading,
    error: chargingStationsError,
  } = useQuery({
    ...getMeasurementsForSiteInArrowOptions({
      path: { site_id: site.site_id },
      body: chargingAssets,
      query: { start: from.toISOString(), stop: to.toISOString() },
    }),
  });

  const chargingStationTimeSeries = useMapResponseToTimeseries(
    chargingStations as Response,
  );

  const bessDevices = deviceDistribution?.battery_energy_storage_system;
  const {
    data: bessSeries,
    isLoading: bessLoading,
    error: bessError,
  } = useQuery({
    ...getMeasurementsForSiteInArrowOptions({
      path: { site_id: site.site_id },
      body: bessDevices,
      query: { start: from.toISOString(), stop: to.toISOString() },
    }),
    enabled: (bessDevices?.length ?? 0) > 0,
  });

  const { powerTimeSeries: bessTimeSeries, socTimeSeries: bessSOCTimeSeries } =
    useMapBessResponseToTimeseries(bessSeries as Response);

  const {
    data: forecastedSeries,
    isLoading: forecastedSeriesLoading,
    error: forecastedSeriesError,
  } = useQuery({
    ...getMeasurementsForSiteInArrowOptions({
      path: { site_id: site.site_id },
      body: deviceDistribution?.forecasted_series_3phase,
      query: { start: from.toISOString(), stop: to.toISOString() },
    }),
  });

  const forecastedDevicesTimeSeries = useMapResponseToTimeseries(
    forecastedSeries as Response,
  );

  const {
    data: collectorsSeries,
    isLoading: collectorsLoading,
    error: collectorsError,
  } = useQuery({
    ...getMeasurementsForSiteInArrowOptions({
      path: { site_id: site.site_id },
      body: collectors,
      query: { start: from.toISOString(), stop: to.toISOString() },
    }),
  });

  const collectorsTimeSeries = useMapResponseToTimeseries(
    collectorsSeries as Response,
  );

  return (
    <div className=" w-full">
      <div className="w-full flex flex-col lg:flex-row lg:space-x-4">
        <div className="flex-1 mb-4 lg:mb-0">
          <h3>Charging stations</h3>
          <TimeSeriesChart
            data={chargingStationTimeSeries}
            title="arrow"
            className="w-full"
            line="smooth"
            isLoading={chargingStationsLoading}
            error={chargingStationsError}
          />
        </div>
        <div className="flex-1 mb-4 lg:mb-0">
          <h3>Collectors</h3>

          <TimeSeriesChart
            data={collectorsTimeSeries}
            title="arrow"
            className="w-full"
            line="smooth"
            isLoading={collectorsLoading}
            error={collectorsError}
          />
        </div>
        {(deviceDistribution?.battery_energy_storage_system?.length ?? 0) >
          0 && (
          <div className="flex-1 mb-4 lg:mb-0">
            <h3>Batteries (BESS)</h3>
            <TimeSeriesChart
              data={bessTimeSeries}
              title="bess"
              className="w-full"
              line="smooth"
              isLoading={bessLoading}
              error={bessError}
            />
            {bessSOCTimeSeries && bessSOCTimeSeries.length > 0 && (
              <>
                <h3>Batteries (BESS) — State of Charge (%)</h3>
                <TimeSeriesChart
                  data={bessSOCTimeSeries}
                  title="bess-soc"
                  className="w-full"
                  line="smooth"
                  isLoading={bessLoading}
                  error={bessError}
                />
              </>
            )}
          </div>
        )}
      </div>

      <div className="w-full grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
        {forecastedDevicesTimeSeries?.map((series, index) => {
          const chartColor = chartColors[index % chartColors.length];
          return (
            <div key={series.name}>
              <h3>{series.name}</h3>
              <TimeSeriesChart
                data={[{ ...series, color: chartColor }]} // Use a distinct color for forecasted series
                title={series.name}
                className="w-full"
                line="smooth"
                isLoading={forecastedSeriesLoading}
                error={forecastedSeriesError}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
